(() => {
  'use strict';

  const normalize = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/direct to film/g, 'dtf')
    .replace(/direct to garment/g, 'dtg')
    .replace(/t[- ]?shirt/g, 'shirt')
    .replace(/print on demand/g, 'pod')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const stopWords = new Set(['a', 'an', 'and', 'are', 'can', 'do', 'does', 'for', 'from', 'how', 'i', 'in', 'is', 'it', 'me', 'my', 'of', 'on', 'or', 'the', 'to', 'what', 'where', 'which', 'why', 'with', 'you', 'your']);
  const synonymSets = [
    ['art', 'artwork', 'design', 'graphic', 'image', 'logo', 'file'],
    ['store', 'storefront', 'shop', 'website'],
    ['url', 'link', 'address', 'website'],
    ['live', 'active', 'activate', 'publish', 'launch', 'preview'],
    ['product', 'item', 'merch', 'merchandise'],
    ['shirt', 'tee'],
    ['method', 'printing', 'print', 'decoration', 'technique'],
    ['embroidery', 'embroider', 'stitched', 'stitch', 'dst'],
    ['price', 'pricing', 'charge', 'markup', 'margin', 'profit', 'earnings'],
    ['administrator', 'admin', 'user', 'login'],
    ['payout', 'disbursement', 'paid', 'deposit', 'ach', 'bank'],
    ['order', 'orders', 'sale', 'purchase'],
    ['buyer', 'customer', 'shopper'],
    ['refund', 'return', 'exchange', 'replacement'],
    ['bulk', 'quantity', 'wholesale'],
    ['soft', 'softness', 'handfeel', 'feel'],
    ['heavy', 'weight', 'gsm', 'ounce', 'ounces'],
    ['blurry', 'pixelated', 'resolution', 'dpi', 'ppi', 'quality'],
    ['transparent', 'transparency', 'background', 'whitebox'],
    ['add', 'build', 'create', 'make'],
    ['missing', 'hidden', 'disappeared', 'invisible', 'showing'],
    ['category', 'categories', 'collection', 'collections'],
    ['login', 'signin', 'password', 'credentials']
  ];
  const synonyms = new Map();
  synonymSets.forEach((set) => set.forEach((word) => synonyms.set(word, set)));

  const debounce = (callback, wait = 90) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), wait);
    };
  };

  const distance = (a, b) => {
    if (Math.abs(a.length - b.length) > 1) return 2;
    const row = Array.from({ length: b.length + 1 }, (_, i) => i);
    for (let i = 1; i <= a.length; i += 1) {
      let previous = row[0];
      row[0] = i;
      for (let j = 1; j <= b.length; j += 1) {
        const current = row[j];
        row[j] = Math.min(row[j] + 1, row[j - 1] + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
        previous = current;
      }
    }
    return row[b.length];
  };

  const words = (value) => normalize(value).split(' ').filter(Boolean);
  const meaningful = (value) => {
    const all = words(value);
    const useful = all.filter((word) => !stopWords.has(word));
    return useful.length ? useful : all;
  };

  const expanded = (token) => synonyms.get(token) || [token];

  const urlKey = (value) => {
    try {
      const url = new URL(String(value || ''), window.location.origin);
      url.hash = '';
      url.pathname = url.pathname.replace(/\/+$/, '') || '/';
      return `${url.origin}${url.pathname}${url.search}`.toLowerCase();
    } catch (error) {
      return String(value || '').replace(/\/+$/, '').toLowerCase();
    }
  };

  class LearningSearch {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-lc4-search-form]');
      this.input = root.querySelector('[data-lc4-search-input]');
      this.clear = root.querySelector('[data-lc4-search-clear]');
      this.panel = root.querySelector('[data-lc4-search-results]');
      this.status = root.querySelector('[data-lc4-search-status]');
      this.active = -1;
      this.results = [];
      try {
        this.index = JSON.parse(root.querySelector('[data-lc4-index]')?.textContent || '[]').map((entry, index) => {
          const title = normalize(entry.title);
          const aliases = normalize(entry.aliases);
          const summary = normalize(entry.summary);
          const body = normalize(entry.body);
          const topic = normalize(entry.topic);
          const type = normalize(entry.type);
          return {
            ...entry,
            index,
            titleSearch: title,
            aliasesSearch: aliases,
            summarySearch: summary,
            bodySearch: body,
            topicSearch: topic,
            typeSearch: type,
            all: `${title} ${aliases} ${summary} ${body} ${topic} ${type}`,
            allWords: words(`${title} ${aliases} ${summary} ${body} ${topic} ${type}`)
          };
        });
      } catch (error) {
        this.index = [];
      }
      this.input?.setAttribute('role', 'combobox');
      this.input?.setAttribute('aria-autocomplete', 'list');
      this.bind();
      this.restore();
    }

    score(entry, rawQuery) {
      const query = normalize(rawQuery);
      const tokens = meaningful(rawQuery);
      if (!query || !tokens.length) return 0;
      let score = 0;
      let covered = 0;

      if (entry.titleSearch === query) score += 700;
      else if (entry.aliasesSearch.split(' ').includes(query)) score += 500;
      else if (entry.titleSearch.startsWith(query)) score += 330;
      else if (entry.titleSearch.includes(query)) score += 240;
      if (entry.aliasesSearch.includes(query)) score += 190;
      if (entry.summarySearch.includes(query)) score += 100;
      if (entry.bodySearch.includes(query)) score += 45;

      tokens.forEach((token) => {
        let best = 0;
        expanded(token).forEach((variant) => {
          if (entry.titleSearch.split(' ').includes(variant)) best = Math.max(best, 80);
          else if (entry.titleSearch.includes(variant)) best = Math.max(best, 58);
          if (entry.aliasesSearch.includes(variant)) best = Math.max(best, 46);
          if (entry.topicSearch.includes(variant) || entry.typeSearch.includes(variant)) best = Math.max(best, 32);
          if (entry.summarySearch.includes(variant)) best = Math.max(best, 24);
          if (entry.bodySearch.includes(variant)) best = Math.max(best, 13);
          if (variant.length >= 4 && entry.allWords.some((candidate) => candidate.startsWith(variant))) best = Math.max(best, 18);
          if (variant.length >= 5 && entry.allWords.some((candidate) => distance(candidate, variant) <= 1)) best = Math.max(best, 12);
        });
        if (best > 0) { covered += 1; score += best; }
      });

      const coverage = covered / tokens.length;
      if (coverage === 1) score += 85;
      else if (coverage >= .66) score += 36;

      const intentBoosts = [
        { pattern: /(dtf.*dtg|dtg.*dtf|why.*dtg|print method|decoration method)/, target: 'print-methods', boost: 700 },
        { pattern: /(dpi|ppi|resolution|blurry|pixel|transparent|white box|vector|raster|line weight)/, target: 'art-requirements', boost: 700 },
        { pattern: /^(art|artwork|logo|graphic|image file|design file|art requirements?|help with (my )?(art|artwork|logo)|will (my )?(artwork|logo) work)$/, target: 'art-requirements', boost: 1000 },
        { pattern: /(^|\s)(art|artwork|logo|graphic|image file|design file|art requirements?)($|\s)/, target: 'art-requirements', boost: 310 },
        { pattern: /(soft|heavy|fabric|material|cotton|polyester|triblend|tri blend|gsm|ounce|ounces|singles|fit|size chart)/, target: 'product-specifications', boost: 700 },
        { pattern: /(bulk|pod|print on demand|screen print transfer|hybrid|inventory)/, target: 'advanced-strategies', boost: 700 },
        { pattern: /(categor(?:y|ies)|collections?|organize.*products|group.*products|product.*sections?)/, target: 'add-product-category', boost: 850 },
        { pattern: /(store url|store link|public store|storefront|make.*live|activate.*store|preview|awaiting approval)/, target: 'get-store-live', boost: 720 },
        { pattern: /((store|storefront|shop).*(isn t|not|won t|cannot|can t|unable|stopped).*(taking|accept|order|checkout|buy)|(can t|cannot|unable).*(order|checkout|buy).*(store|shop)|(order|checkout).*(disabled|off|closed|not working))/, target: 'get-store-live', boost: 950 },
        { pattern: /(forgot.*password|reset.*password|password.*reset|can t.*(sign in|log in|login)|cannot.*(sign in|log in|login)|sign in|log in|login|invitation|invited email|wrong google|find.*account|find.*store)/, target: 'sign-in-find-store', boost: 820 },
        { pattern: /(edit.*product|fix.*product|change.*design|change.*art|change.*color|rename.*product|edit.*description|primary color)/, target: 'edit-polish-product', boost: 720 },
        { pattern: /(share.*store|announce.*store|copy.*link|store qr)/, target: 'share-your-store', boost: 700 },
        { pattern: /(first sale|first order|after.*sale|after.*order)/, target: 'after-first-sale', boost: 760 },
        { pattern: /(refund|return|exchange|cancel.*order|order.*cancel|where.*(order|package)|track.*(order|package)|(order|package).*(track|late|missing|not arrived)|damaged.*item|wrong.*item|buyer help|customer support|customer service|help.*(order|purchase)|(order|purchase).*help|(bought|purchased).*(help|problem|issue))/, target: 'printyourcause.zendesk.com', boost: 1000 },
        { pattern: /(administrator|admin|company address|billing|disbursement|bank|payout setting)/, target: 'set-up-account', boost: 700 },
        { pattern: /(notification|order email|order alert|who.*receives.*order|who.*gets.*order)/, target: 'order-notifications', boost: 900 },
        { pattern: /(first product|add.*product|make.*shirt|build.*product|upload.*art|set.*price)/, target: 'build-your-first-product', boost: 720 },
        { pattern: /(product.*(missing|not showing|invisible)|missing.*product|store.*empty)/, target: 'product-not-showing', boost: 850 },
        { pattern: /(change|edit|set).*(product )?price|fixed price markup/, target: 'change-product-price', boost: 850 },
        { pattern: /(embroidery|embroider).*(reject|accept|upload|file|art|logo|dst)/, target: 'embroidery-file-help', boost: 850 }
      ];
      let matchedIntent = false;
      intentBoosts.forEach(({ pattern, target, boost }) => {
        if (pattern.test(query) && String(entry.url).toLowerCase().includes(target)) {
          score += boost;
          matchedIntent = true;
        }
      });

      const storeCannotOrder = /((store|storefront|shop).*(isn t|not|won t|cannot|can t|unable|stopped).*(taking|accept|order|checkout|buy)|(can t|cannot|unable).*(order|checkout|buy).*(store|shop)|(order|checkout).*(disabled|off|closed|not working))/.test(query);
      if (storeCannotOrder && String(entry.url).includes('order-notifications')) score -= 600;
      const buyerHelp = /(refund|return|exchange|cancel.*order|where.*(order|package)|track.*(order|package)|(order|package).*(track|late|missing|not arrived)|damaged.*item|wrong.*item|buyer help|customer support|customer service|help.*(order|purchase)|(order|purchase).*help)/.test(query);
      if (buyerHelp && (String(entry.url).includes('after-first-sale') || String(entry.url).includes('orders-shipping-payouts'))) score -= 320;
      if (tokens.length > 1 && coverage < .5 && !matchedIntent) return -1;
      return score;
    }

    search(rawQuery) {
      if (normalize(rawQuery).length < 2) return [];
      const seen = new Set();
      return this.index
        .map((entry) => ({ entry, score: this.score(entry, rawQuery) }))
        .filter((result) => result.score > 0)
        .sort((a, b) => b.score - a.score || a.entry.index - b.entry.index)
        .filter((result) => {
          const key = urlKey(result.entry.url);
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .slice(0, 7)
        .map((result) => result.entry);
    }

    render() {
      const query = this.input?.value.trim() || '';
      this.results = this.search(query);
      this.active = -1;
      if (this.clear) this.clear.hidden = !query;
      if (!this.panel || !this.input) return;

      if (normalize(query).length < 2) {
        this.close();
        return;
      }

      this.panel.replaceChildren();
      if (!this.results.length) {
        const empty = document.createElement('div');
        empty.className = 'f5-lc4-result-empty';
        empty.innerHTML = '<strong>No close match yet.</strong><span>Try fewer words, browse the sections below, or <a href="/pages/help">open Help</a> for store-owner and order support.</span>';
        this.panel.appendChild(empty);
      } else {
        this.results.forEach((entry, index) => {
          const link = document.createElement('a');
          link.className = 'f5-lc4-result';
          link.href = entry.url;
          link.id = `lc4-option-${index}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');

          const title = document.createElement('strong');
          title.textContent = entry.title;
          const summary = document.createElement('small');
          summary.textContent = entry.summary;
          const meta = document.createElement('em');
          meta.textContent = `${entry.type} · ${entry.topic}`;
          link.append(title, summary, meta);
          link.addEventListener('click', () => this.track('learning_center_search_select', { search_term: query, result_title: entry.title, result_rank: index + 1 }));
          this.panel.appendChild(link);
        });
      }
      if (this.status) this.status.textContent = this.results.length === 1 ? '1 result found.' : `${this.results.length} results found.`;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    move(direction) {
      if (!this.results.length || this.panel?.hidden) return;
      const options = [...this.panel.querySelectorAll('[role="option"]')];
      this.active = (this.active + direction + options.length) % options.length;
      options.forEach((option, index) => {
        const selected = index === this.active;
        option.classList.toggle('is-active', selected);
        option.setAttribute('aria-selected', String(selected));
      });
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
      options[this.active].scrollIntoView({ block: 'nearest' });
    }

    close() {
      if (this.panel) this.panel.hidden = true;
      this.input?.setAttribute('aria-expanded', 'false');
      this.input?.removeAttribute('aria-activedescendant');
      this.active = -1;
    }

    submit() {
      const query = this.input?.value.trim() || '';
      this.results = this.search(query);
      this.track('learning_center_search', { search_term: query, result_count: this.results.length });
      const url = new URL(window.location.href);
      if (query) url.searchParams.set('q', query); else url.searchParams.delete('q');
      window.history.replaceState({}, '', url);
      this.render();
      if (this.status && this.results.length) this.status.textContent = `${this.results.length} results shown, ordered by relevance. Use the arrow keys to review them.`;
    }

    track(eventName, parameters) {
      if (typeof window.gtag === 'function') window.gtag('event', eventName, parameters);
    }

    bind() {
      this.form?.addEventListener('submit', (event) => { event.preventDefault(); this.submit(); });
      this.input?.addEventListener('input', debounce(() => this.render()));
      this.input?.addEventListener('focus', () => { if (this.input.value.trim()) this.render(); });
      this.input?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') { event.preventDefault(); this.move(1); }
        else if (event.key === 'ArrowUp') { event.preventDefault(); this.move(-1); }
        else if (event.key === 'Escape') { this.close(); }
        else if (event.key === 'Enter') {
          event.preventDefault();
          if (this.active >= 0) this.panel?.querySelectorAll('[role="option"]')[this.active]?.click();
          else this.submit();
        }
      });
      this.clear?.addEventListener('click', () => {
        this.input.value = '';
        this.close();
        this.clear.hidden = true;
        const url = new URL(window.location.href);
        url.searchParams.delete('q');
        window.history.replaceState({}, '', url);
        this.input.focus();
      });
      document.addEventListener('pointerdown', (event) => {
        if (!this.form?.contains(event.target) && !this.panel?.contains(event.target)) this.close();
      });
    }

    restore() {
      if (!this.input) return;
      const query = new URLSearchParams(window.location.search).get('q');
      if (!query) return;
      this.input.value = query;
      this.render();
    }
  }

  class LearningArticle {
    constructor(root) {
      this.root = root;
      const nav = root.querySelector('[data-la4-toc]');
      const list = root.querySelector('[data-la4-toc-list]');
      const sections = [...root.querySelectorAll('[data-la4-section]')];
      if (!nav || !list || sections.length < 2) {
        if (nav) nav.hidden = true;
        return;
      }
      const links = sections.map((section, index) => {
        if (!section.id) section.id = `section-${index + 1}`;
        const link = document.createElement('a');
        link.href = `#${section.id}`;
        link.textContent = section.querySelector('h2')?.textContent.trim() || `Section ${index + 1}`;
        const item = document.createElement('li');
        item.appendChild(link);
        list.appendChild(item);
        return link;
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            links.forEach((link) => {
              const active = link.hash === `#${entry.target.id}`;
              link.classList.toggle('is-active', active);
              if (active) link.setAttribute('aria-current', 'location');
              else link.removeAttribute('aria-current');
            });
          });
        }, { rootMargin: '-15% 0px -72% 0px' });
        sections.forEach((section) => observer.observe(section));
      }
    }
  }

  const boot = () => {
    document.querySelectorAll('[data-lc4-root]').forEach((root) => new LearningSearch(root));
    document.querySelectorAll('[data-la4-root]').forEach((root) => new LearningArticle(root));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
